import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * AdminPesdbAudit — "Giocatori non-standard".
 * Scansiona i giocatori in rosa agganciati e segnala quelli la cui scheda PESDB NON è nella
 * lista standard/autentica (es. Mor Talla Ndiaye): vanno scollegati per usare la scheda no-PESDB.
 */
export default function AdminPesdbAudit() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(null);
    const pollRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/pesdb-audit/status");
            setData(data);
            return data;
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
            return null;
        }
    };

    useEffect(() => {
        load();
        return () => clearInterval(pollRef.current);
    }, []);

    // Polla mentre la scansione è in corso
    useEffect(() => {
        clearInterval(pollRef.current);
        if (data?.running) {
            pollRef.current = setInterval(load, 3000);
        }
        return () => clearInterval(pollRef.current);
    }, [data?.running]);

    const startScan = async () => {
        setErr(null);
        setBusy("scan");
        try {
            await api.post("/admin/pesdb-audit/scan");
            await new Promise((r) => setTimeout(r, 800));
            await load();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(null);
        }
    };

    const unlink = async (teamId, entryId) => {
        if (!window.confirm("Scollegare la scheda PESDB da questo giocatore?\n\nResterà in rosa col proprio nome/dati Transfermarkt, senza scheda PESDB.")) return;
        setBusy(`rm:${entryId}`);
        setErr(null);
        try {
            await api.post(`/teams/${teamId}/roster/${entryId}/admin-unlink-pesdb`);
            // rimuovi localmente questa voce dai sospetti
            setData((d) => {
                if (!d) return d;
                const suspects = d.suspects
                    .map((s) => ({ ...s, entries: s.entries.filter((e) => e.entry_id !== entryId) }))
                    .filter((s) => s.entries.length > 0);
                return { ...d, suspects };
            });
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(null);
        }
    };

    const stopScan = async () => {
        setBusy("stop");
        try {
            await api.post("/admin/pesdb-audit/stop");
            await load();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(null);
        }
    };

    const pct = data?.total ? Math.round((data.scanned / data.total) * 100) : 0;
    const suspects = data?.suspects || [];

    return (
        <div data-testid="admin-pesdb-audit-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-ff9d2b))" }}>// VERIFICA STANDARD</div>
            <h2 className="op-heading text-3xl mt-2 mb-2">Giocatori non-standard</h2>
            <p className="op-mono text-xs text-t8f95a3 mb-4 max-w-3xl leading-relaxed">
                Cerca i giocatori in rosa agganciati a una scheda PESDB che <strong className="text-tffffff">non è nella lista
                standard/autentica</strong> (presente solo in "tutti i giocatori" → non realmente nel gioco, come Mor Talla Ndiaye).
                Per ognuno usa <span className="text-tff6b6b">Scollega</span>: il giocatore resta in rosa con i dati Transfermarkt,
                ma senza la scheda PESDB sbagliata. La scansione interroga pesdb.net che limita molto le richieste:
                è quindi <strong className="text-tffffff">lenta (anche ~1 ora)</strong> ma gira in background, si può
                <strong className="text-tffffff"> fermare e riprendere</strong>, e non rifà i giocatori già verificati.
            </p>

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2 mb-3" data-testid="audit-err">{err}</div>
            )}

            <div className="op-card p-4 mb-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <button
                            onClick={startScan}
                            disabled={busy === "scan" || data?.running}
                            className="op-mono text-xs uppercase tracking-wider px-4 py-2 border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff/10 transition-colors disabled:opacity-40"
                            data-testid="audit-scan-btn"
                        >
                            {data?.running ? "Scansione in corso…" : busy === "scan" ? "Avvio…" : "▶ Avvia / riprendi scansione"}
                        </button>
                        {data?.running && (
                            <button
                                onClick={stopScan}
                                disabled={busy === "stop"}
                                className="op-mono text-xs uppercase tracking-wider px-4 py-2 border border-tff6b6b text-tff6b6b hover:bg-tff6b6b/10 transition-colors disabled:opacity-40 ml-2"
                                data-testid="audit-stop-btn"
                            >
                                {busy === "stop" ? "..." : "■ Ferma"}
                            </button>
                        )}
                        {data?.finished_at && !data?.running && (
                            <span className="op-mono text-[10px] text-t5c6370 ml-3">
                                {data?.stopped ? "Fermata" : "Completata"} · {suspects.length} sospetti · errori rete: {data?.errors ?? 0}
                            </span>
                        )}
                    </div>
                    {(data?.running || data?.scanned > 0) && (
                        <div className="op-mono text-[11px] text-t8f95a3" data-testid="audit-progress">
                            {data.scanned}/{data.total} ({pct}%)
                        </div>
                    )}
                </div>
                {(data?.running || data?.scanned > 0) && (
                    <div className="h-1.5 bg-t22252a mt-3 rounded-sm overflow-hidden">
                        <div className="h-full bg-t2bb6ff transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                )}
            </div>

            {/* Lista sospetti */}
            <div className="op-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-t8f95a3 border-b border-t22252a">
                        <tr>
                            <th className="py-2 px-3 text-left">Giocatore</th>
                            <th className="py-2 px-3 text-left">PES ID</th>
                            <th className="py-2 px-3 text-left">Squadra OP</th>
                            <th className="py-2 px-3 text-right">Azione</th>
                        </tr>
                    </thead>
                    <tbody>
                        {suspects.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="py-6 px-3 text-center op-mono text-xs text-t5c6370">
                                    {data?.running ? "Scansione in corso…" : "Nessun giocatore sospetto trovato (avvia una scansione)."}
                                </td>
                            </tr>
                        ) : (
                            suspects.flatMap((s) =>
                                s.entries.map((e) => (
                                    <tr key={e.entry_id} className="border-b border-t1a1d24 hover:bg-t0f1115" data-testid={`suspect-${e.entry_id}`}>
                                        <td className="py-2 px-3 text-tffffff">{e.display_name || s.name}</td>
                                        <td className="py-2 px-3 op-mono text-t8f95a3">
                                            <a href={`https://pesdb.net/efootball/?id=${s.pes_id}`} target="_blank" rel="noreferrer" className="text-t2bb6ff hover:underline">
                                                {s.pes_id}
                                            </a>
                                        </td>
                                        <td className="py-2 px-3 op-mono text-tc9ccd3">{e.team_name}</td>
                                        <td className="py-2 px-3 text-right">
                                            <button
                                                onClick={() => unlink(e.team_id, e.entry_id)}
                                                disabled={busy === `rm:${e.entry_id}`}
                                                className="op-mono text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors disabled:opacity-40"
                                                style={{ borderColor: "rgb(var(--tc-ff3b30) / 0.333)", color: "rgb(var(--tc-ff6b6b))" }}
                                                data-testid={`unlink-${e.entry_id}`}
                                            >
                                                {busy === `rm:${e.entry_id}` ? "..." : "Scollega"}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )
                        )}
                    </tbody>
                </table>
            </div>

            <div className="op-mono text-[10px] text-t5c6370 leading-relaxed mt-4">
                ⚠ Lo "Scollega" agisce solo su <strong>questo ambiente</strong>. Per applicare in produzione, ripeti lì la scansione
                e lo scollegamento (oppure usa il ponte dati). Il link sul PES ID apre la scheda su pesdb.net per una verifica manuale.
            </div>
        </div>
    );
}
